import type { FC } from 'react';
import { Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import useGetOptimisedResults from '../../hooks/queries/useGetOptimisedResults';
import OptimiseResultSummary from './OptimiseResultSummary';
import OptimiseResultTable from './OptimiseResultTable';
import {
    TPO_OPTIMISE_RESULT_ERROR,
    TPO_OPTIMISE_RESULT_LOADING,
    TPO_OPTIMISE_RESULT_MODAL,
} from '../../../../config/testIds';
import { useTpoGenConfig } from '../../../../context/CustomerConfigContext';

interface Props {
    open: boolean;
    onClose: () => void;
    jobId: string | null;
    planId: string;
}

const MODAL_WIDTH = 940;

const OptimiseResultModal: FC<Props> = ({ open, onClose, jobId, planId }) => {
    const tpoGenConfig = useTpoGenConfig();
    const { data, isFetching, isError } = useGetOptimisedResults(open ? jobId : null, planId);

    const config = tpoGenConfig.optimizeResultConfig;

    const isTopLevel =
        tpoGenConfig.optimizePlanConfig.constraintLevelOptions.find(
            (o) => o.key === data?.summary.optimization_attribute_level,
        )?.depthLevel === 0;

    const showResults = !isFetching && !isError && data;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            title={<span className="text-base font-semibold text-[#18181b]">Optimization Results</span>}
            footer={null}
            width={MODAL_WIDTH}
            destroyOnClose
            centered
            bodyStyle={{ height: '70vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', paddingTop: 16 }}
        >
            {isFetching ? (
                <div className="flex items-center justify-center py-16" data-testid={TPO_OPTIMISE_RESULT_LOADING}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 16, color: 'red' }} spin />} />
                </div>
            ) : null}

            {isError && !isFetching ? (
                <div
                    className="flex items-center justify-center py-16 text-[#ff4d4f] text-sm"
                    data-testid={TPO_OPTIMISE_RESULT_ERROR}
                >
                    {'Failed to load optimization results. Please try again.'}
                </div>
            ) : null}

            {showResults ? (
                <div className="flex flex-col h-full min-h-0" data-testid={TPO_OPTIMISE_RESULT_MODAL}>
                    <div className="shrink-0 mb-3">
                        <OptimiseResultSummary summary={data.summary} config={config} />
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto">
                        <OptimiseResultTable results={data.results} config={config} isTopLevel={isTopLevel} />
                    </div>
                </div>
            ) : null}
        </Modal>
    );
};

OptimiseResultModal.displayName = 'OptimiseResultModal';
export default OptimiseResultModal;
